// /api/admin/license — read status, apply envelope, drop license.

import useSWR from 'swr';

export type LicenseState =
  | 'trial_active'
  | 'trial_expired'
  | 'licensed_active'
  | 'licensed_expired'
  | 'invalid';

export type LicenseKind = 'trial' | 'commercial' | 'internal';

export interface LicenseStatus {
  state: LicenseState;
  kind: LicenseKind;
  customer: string;
  license_id: string;
  install_id: string;
  first_boot: string;
  not_before?: string;
  expires_at?: string;
  days_remaining: number;
  revoked: boolean;
  invalid_reason?: string;
  features: Record<string, boolean>;
}

export interface LicenseEvent {
  at: string;
  kind: string;
  license_id?: string;
  actor?: string;
  detail?: Record<string, unknown>;
}

const fetcher = (url: string) =>
  fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useLicense() {
  return useSWR<LicenseStatus>('/api/admin/license', fetcher, {
    refreshInterval: 60_000,
  });
}

export function useLicenseEvents(limit = 50) {
  return useSWR<{ events: LicenseEvent[] }>(`/api/admin/license/events?limit=${limit}`, fetcher);
}

export async function applyLicense(envelope: string): Promise<LicenseStatus> {
  const res = await fetch('/api/admin/license', {
    method: 'PUT',
    headers: { 'Content-Type': 'text/plain' },
    credentials: 'include',
    body: envelope,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function removeLicense(): Promise<void> {
  const res = await fetch('/api/admin/license', {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(await res.text());
}

// /api/v1/license/features — feature catalogue + live entitlement matrix
// rendered as the "What this license unlocks" card on the AdminLicense
// page. Read-only; safe to call on expired-trial installs.

export type FeatureCategory =
  | 'core'
  | 'security'
  | 'operations'
  | 'auth'
  | 'replication'
  | 'automation'
  | 'migration';

export type FeatureTier = 'free' | 'commercial';

export interface FeatureRow {
  id: string;
  label: string;
  description: string;
  category: FeatureCategory;
  tier: FeatureTier;
  unlocked_in_states: LicenseState[];
  real_world_impact: string;
  currently_unlocked: boolean;
  trial_locks_in_days?: number;
}

export interface ProcurementInfo {
  email?: string;
  url?: string;
}

export interface FeatureMatrix {
  current_state: LicenseState;
  kind: LicenseKind;
  customer?: string;
  license_id?: string;
  install_id: string;
  expires_at?: string;
  days_remaining: number;
  features: FeatureRow[];
  procurement: ProcurementInfo;
}

export function useLicenseFeatures() {
  return useSWR<FeatureMatrix>('/api/v1/license/features', fetcher, {
    refreshInterval: 60_000,
  });
}
